<template>
  <div class="p-5 pt-3">
    <div class="flex flex-wrap flex-row justify-between items-center">
      <p class="font-semibold text-3xl">
        Detail {{ startCase(this.$route.path.split('/')[4]) }} Sales Plan
      </p>
      <vs-button
        class="log-button"
        color="secondary"
        @click="isSideCarShown = true"
      >
        Log
      </vs-button>
    </div>
    <data-table
      class="mt-8"
      :loading="isLoading"
      :table-header="tableHeaders"
      :table-data="transformData(tableData)"
      :sort-by="filter.sortBy"
      :sort-dir="filter.sortDir"
      @sort="sort"
    >
      <template #data="{ data, index }">
        <td
          class="td vs-table--td"
          :key="key"
          v-for="key in tableHeaders.reduce((acc,curr) => [...acc, curr.key], [])"
          :class="{
            'row-primary': data[index].port.includes('Total'),
            'row-secondary': data[index].port.includes('Grand Total')
          }"
        >
          <span>
            <div
              v-if="data[index].port.includes('Total')"
              :class="{
                'whitespace-no-wrap': true,
                'invisible': key === 'mine' || key === 'source'
              }"
            >
              {{ number(data[index][key]) }}
            </div>
            <div
              v-else
              :class="{
                'whitespace-no-wrap': true,
                'invisible': isInvisible(index, key, data),
                'text-secondary font-semibold italic': key === 'mine'
              }"
            >
              {{ number(data[index][key]) }}
            </div>
          </span>
        </td>
      </template>
    </data-table>
    <log-sidecar
      :is-shown.sync="isSideCarShown"
      :log-data="getLogTypeData()"
      @see="seeHistory"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import LogSidecar from '@/components/logSidecar'
import { PAGE_TITLE } from '@/fixtures/Constants'
import { isNumber, sortData,transformData } from '@/utils/salesPlanUtils'

export default {
  metaInfo: {
    title: `${PAGE_TITLE} Sales Plan Detail`
  },
  components: {
    DataTable,
    LogSidecar
  },
  data() {
    return {
      isSideCarShown: false,
      isLoading: false,
      filter: {
        sortBy: '',
        sortDir: null
      },
      loading: false,
      tableHeaders: [
        { key: 'port', label: 'Port', sortable: true },
        { key: 'mine', label: 'Mine' },
        { key: 'source', label: 'Source' },
        { key: 'jan', label: 'Jan' },
        { key: 'feb', label: 'Feb' },
        { key: 'mar', label: 'Mar' },
        { key: 'apr', label: 'Apr' },
        { key: 'may', label: 'May' },
        { key: 'jun', label: 'Jun' },
        { key: 'jul', label: 'Jul' },
        { key: 'aug', label: 'Aug' },
        { key: 'sep', label: 'Sep' },
        { key: 'oct', label: 'Oct' },
        { key: 'nov', label: 'Nov' },
        { key: 'dec', label: 'Dec' },
        { key: 'grand_total', label: 'Grand Total' }
      ],
      tableData: []
    }
  },
  computed: {
    ...mapGetters({
      detailId: 'SalesPlan/salesPlanDetailId',
      details: 'SalesPlan/salesPlanDetail',
      annualPeriod: 'SalesPlan/salesPlanAnnualPeriod',
      annualLogs: 'SalesPlan/salesPlanAnnualLogs',
      monthlyPeriod: 'SalesPlan/salesPlanMonthlyPeriod',
      monthlyLogs: 'SalesPlan/salesPlanMonthlyLogs'
    })
  },
  mounted() {
    this.fetchSalesPlanDetail()

    if (this.$route.name === 'SalesPlanDetailMonthly') {
      this.fetchMonthlyLog()
      return
    }

    this.fetchAnnualLog()
  },
  watch: {
    details: {
      immediate: true,
      handler() {
        this.tableData = this.details ? sortData(this.details, 'id', 'asc') : []
      }
    }
  },
  methods: {
    transformData,
    isInvisible(index, key, data) {
      if (index && (key === 'port' || key === 'mine')) {
        return data[index][key] === data[index-1][key]
      }
      return false
    },
    async fetchSalesPlanDetail() {
      try {
        this.isLoading = true
        await this.$store.dispatch('SalesPlan/getSalesPlanDetail', this.$route.params.id || this.detailId)

        this.isLoading = false
      } catch (e) {
        this.isLoading = false
        this.$vs.notify({
          title: 'Failed',
          text: 'Something went wrong',
          color: 'danger',
          icon: 'close',
          position: 'top-right'
        })
      }
    },
    async fetchMonthlyLog() {
      try {
        await this.$store.dispatch('SalesPlan/getSalesPlanMonthlyLog', this.$route.params.period || this.monthlyPeriod)
      } catch (e) {
        this.showFailedNotification(this, 'Cannot fetch monthly logs')
      }
    },
    async fetchAnnualLog() {
      try {
        await this.$store.dispatch('SalesPlan/getSalesPlanAnnualLog', this.$route.params.period || this.annualPeriod)
      } catch (e) {
        this.showFailedNotification(this, 'Cannot fetch annual logs')
      }
    },
    sort(sortBy, sortDir) {
      this.isLoading = true
      this.filter.sortBy = sortBy
      this.filter.sortDir = sortDir
      this.tableData = !this.filter.sortDir
        ? sortData(this.details, 'id', 'asc')
        : sortData(this.details, sortBy, sortDir)
      this.isLoading = false
    },
    number(number) {
      return isNumber(number)
        ? number.toLocaleString('id-ID')
        : number
    },
    getLogTypeData() {
      return this.$route.name === 'SalesPlanDetailMonthly'
        ? this.monthlyLogs
        : this.annualLogs
    },
    seeHistory(info) {
      // If we push to same route, vue detect it as redundant navigation, so we push to blank path first
      this.$router.push('/')
        .then(() => this.$router.push(info))
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.log-button {
  @apply py-4 #{!important};
  @apply px-16;
  @apply rounded-xl;
  -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);

  &:focus {
    @apply border-none;
    @apply outline-none;
  }
}
</style>
